import React, { useState, useEffect } from 'react';
import { Table, Card, message, Spin, Form, Button, Row, Col, Modal, Popconfirm, Input, DatePicker, InputNumber, Cascader, Space, Tag, Divider } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { SearchOutlined, EditOutlined, DeleteOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import addressOptions from 'china-division/dist/pca.json';
import ResizableTable from '../components/ResizableTable';

// 转换省市区数据为 Cascader 所需的格式
const cascaderOptions = Object.keys(addressOptions).map(province => ({
  value: province,
  label: province,
  children: Object.keys(addressOptions[province]).map(city => ({
    value: city,
    label: city,
    children: addressOptions[province][city].map(district => ({
      value: district,
      label: district
    }))
  }))
}));

const CustomerList = () => {
  // 设置 dayjs 为中文
  dayjs.locale('zh-cn');
  const [customers, setCustomers] = useState([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchForm] = Form.useForm();
  const [isCreating, setIsCreating] = useState(false);

  // pagination and search params for server-side pagination
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchParams, setSearchParams] = useState({});

  // 添加列状态管理
  const [columns, setColumns] = useState([
    {
      title: '校区名称',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 250,
      sorter: (a, b) => {
        const nameA = a.customerName || '';
        const nameB = b.customerName || '';
        return nameA.localeCompare(nameB);
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="搜索校区名称"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            搜索
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            重置
          </Button>
        </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => {
        const name = record.customerName || '';
        return name.toLowerCase().includes(value.toLowerCase());
      },
    },
    {
      title: '有效期限',
      dataIndex: 'validStartTime',
      key: 'validStartTime',
      width: 150,
      sorter: (a, b) => {
        const dateA = new Date(a.validEndTime);
        const dateB = new Date(b.validEndTime);
        return dateA - dateB;
      },
      render: (text, record) => dayjs(record.validEndTime).format('YYYY-MM-DD'),
    },
    {
      title: '校区地址',
      dataIndex: 'customerAddress',
      key: 'customerAddress',
      width: 300,
      sorter: (a, b) => {
        const addrA = a.customerAddress || '';
        const addrB = b.customerAddress || '';
        return addrA.localeCompare(addrB);
      },
    },
    {
      title: '联系方式(负责人)',
      dataIndex: 'phoneDecimal',
      key: 'phoneDecimal',
      width: 200,
      sorter: (a, b) => {
        const phoneA = a.phoneDecimal || '';
        const phoneB = b.phoneDecimal || '';
        return phoneA.localeCompare(phoneB);
      },
      render: (text) => (
        <Space wrap>
          {(text || '').split(';').filter(item => item.trim()).map((phone, index) => (
            <Tag key={index} color="blue">{phone}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'nowrap' }}>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ fontSize: '12px' }}
          >
            修改
          </Button>
          <Popconfirm
            title="确认删除"
            description={`确定要删除校区"${record.customerName}"吗？`}
            onConfirm={() => handleDelete(record)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="primary"
              size="small"
              icon={<DeleteOutlined />}
              danger
              style={{ fontSize: '12px' }}
            >
              删除
            </Button>
          </Popconfirm>
        </div>
      ),
    }
  ]);

  const { apiClient } = useAuth();

  useEffect(() => {
    fetchCustomers({ pageNum: 1, pageSize: pagination.pageSize });
  }, []);

  // Fetch customers from server with pagination and optional search filters
  const fetchCustomers = async ({ pageNum = 1, pageSize = 10, searchContent } = {}) => {
    if (!apiClient) {
      console.error('API客户端未初始化');
      message.error('系统初始化中，请稍后重试');
      return;
    }

    setLoading(true);
    try {
      const params = {
        pageNum,
        pageSize,
        ...(searchContent ? { searchContent } : {}),
      };

      const response = await apiClient.get('/sports-base-server-prod/myPage/searchCustomer', { params });

      if (response.data && response.data.result === '1') {
        const list = response.data.customerList || [];
        const total = response.data.total || 0;
        setCustomers(list);
        setPagination(prev => ({ ...prev, current: pageNum, pageSize, total }));
      } else {
        message.error(response.data?.msg || '获取校区列表失败');
      }
    } catch (error) {
      console.error('获取校区列表错误:', error);
      message.error('获取校区列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // When search form is submitted, save search params and fetch first page with those params
  const handleSearch = (values) => {
    setSearchParams(values || {});
    // fetch first page with provided search params
    fetchCustomers({ pageNum: 1, pageSize: pagination.pageSize, ...values });
  };

  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({});
    // reset to first page and refetch
    fetchCustomers({ pageNum: 1, pageSize: pagination.pageSize });
  };

  // 编辑功能：打开编辑表单
  const handleEdit = (record) => {
    // 拆分地址：省市区 详细地址
    const addressParts = record.customerAddress ? record.customerAddress.split(' ') : ['', ''];
    const provinceCityDistrict = addressParts.slice(0, 3); // 前三个部分作为省市区
    const detailAddress = addressParts.slice(3).join(' '); // 剩余部分作为详细地址

    // 分割联系方式字符串为数组
    const phoneNumbers = record.phoneDecimal ? record.phoneDecimal.split(';').filter(item => item.trim()) : [];

    editForm.setFieldsValue({
      customerId: record.customerId,
      customerName: record.customerName,
      provinceCityDistrict: provinceCityDistrict,
      detailAddress: detailAddress,
      phoneDecimal: phoneNumbers,
      validStartTime: dayjs(record.validStartTime),
      validEndTime: dayjs(record.validEndTime),
      userLimitQuantum: record.userLimitQuantum,
      lng: record.lng,
      lat: record.lat,
    });
    setIsCreating(false);
    setEditModalVisible(true);
  };

  // 新增校区：打开编辑表单（创建模式）
  const handleAdd = () => {
    editForm.resetFields();
    // 初始化一个空的联系方式数组
    editForm.setFieldsValue({ phoneDecimal: [''] });
    setIsCreating(true);
    setEditModalVisible(true);
  };

  // 保存编辑后的信息
  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();

      // 组装地址：省市区 + 详细地址，用半角空格分隔
      const provinceCityDistrict = values.provinceCityDistrict || [];
      const detailAddress = values.detailAddress || '';
      const customerAddress = [...provinceCityDistrict, detailAddress].filter(Boolean).join(' ');

      // 处理联系方式：将数组转换为用分号分隔的字符串
      let phoneDecimalStr = '';
      if (Array.isArray(values.phoneDecimal)) {
        phoneDecimalStr = values.phoneDecimal.filter(item => item && item.trim()).join(';');
      } else {
        phoneDecimalStr = values.phoneDecimal || '';
      }

      if (isCreating) {
        // 新增校区
        const params = {
          phoneDecimal: phoneDecimalStr,
          customerName: values.customerName,
          validEndTime: values.validEndTime.format('YYYY-MM-DD'),
          customerAddress: customerAddress,
        };

        setLoading(true);
        const response = await apiClient.get('/sports-base-server-prod/myPage/invite', { params });

        if (response.data && response.data.result === '1') {
          message.success('新增成功');
          setEditModalVisible(false);
          setIsCreating(false);
          // refresh current page
          fetchCustomers({ pageNum: pagination.current, pageSize: pagination.pageSize, ...searchParams });
        } else {
          message.error(response.data?.msg || '新增失败');
        }
      } else {
        // 修改校区
        const payload = {
          customerId: values.customerId,
          customerName: values.customerName,
          customerAddress: customerAddress,
          phoneDecimal: phoneDecimalStr,
          validStartTime: values.validStartTime.format('YYYY-MM-DD'),
          validEndTime: values.validEndTime.format('YYYY-MM-DD'),
          userLimitQuantum: values.userLimitQuantum,
          lng: values.lng,
          lat: values.lat,
        };

        setLoading(true);
        const response = await apiClient.post('/sports-base-server-prod/myPage/updateCustomer', payload);

        if (response.data && response.data.result === '1') {
          message.success('修改成功');
          setEditModalVisible(false);
          setIsCreating(false);
          // refresh current page
          fetchCustomers({ pageNum: pagination.current, pageSize: pagination.pageSize, ...searchParams });
        } else {
          message.error(response.data?.msg || '修改失败');
        }
      }
    } catch (err) {
      if (err.errorFields) {
        // validation error
        return;
      }
      console.error('保存编辑错误:', err);
      message.error('保存失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 删除确认
  const handleDelete = async (record) => {
    try {
      setLoading(true);
      const response = await apiClient.get('/sports-base-server-prod/myPage/deleteCustomer', {
        params: { customerId: record.customerId }
      });

      if (response.data && response.data.result === '1') {
        message.success(`删除成功: ${record.customerName}`);
        // 刷新当前页数据
        fetchCustomers({ pageNum: pagination.current, pageSize: pagination.pageSize, ...searchParams });
      } else {
        message.error(response.data?.msg || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="menu-item-content">
      <Card title={`校区列表 (共 ${pagination.total} 条)`} style={{ margin: '16px' }}>
        {/* 搜索区域 */}
        <Form
          form={searchForm}
          layout="vertical"
          onFinish={handleSearch}
          style={{ marginBottom: '20px' }}
        >
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Form.Item label="关键字搜索" name="searchContent">
                <Input placeholder="关键字搜索" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={8}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增</Button>
            </Col>
            <Col span={16} style={{ textAlign: 'right' }}>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button style={{ marginLeft: '8px' }} onClick={handleReset}>
                重置
              </Button>
            </Col>
          </Row>
        </Form>

        <Spin spinning={loading}>
          <ResizableTable
            dataSource={customers}
            columns={columns}
            onColumnsChange={setColumns}
            rowKey="customerId"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
            }}
            onChange={(pag) => {
              // update pagination state and fetch the new page with current search params
              setPagination(prev => ({ ...prev, current: pag.current, pageSize: pag.pageSize }));
              fetchCustomers({ pageNum: pag.current, pageSize: pag.pageSize, ...searchParams });
            }}
          />
        </Spin>
      </Card>

      {/* 编辑弹窗 */}
      <Modal
        title={isCreating ? "新增校区" : "编辑校区"}
        open={editModalVisible}
        onCancel={() => { setEditModalVisible(false); setIsCreating(false); }}
        footer={[
          <Button key="cancel" onClick={() => { setEditModalVisible(false); setIsCreating(false); }}>
            取消
          </Button>,
          <Button key="save" type="primary" loading={loading} onClick={handleSaveEdit}>
            保存
          </Button>
        ]}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          initialValues={{
            validStartTime: dayjs(),
            userLimitQuantum: 999,
          }}
          onFinish={handleSaveEdit}
        >
          {!isCreating && (
            <Row gutter={12}>
              <Col span={24}>
                <Form.Item label="校区ID" name="customerId">
                  <Input disabled />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={12}>
            <Col span={24}>
              <Form.Item
                label="校区名称"
                name="customerName"
                rules={[
                  { required: true, message: '请输入校区名称' },
                  { max: 30, message: '校区名称不能超过30个字符' }
                ]}
              >
                <Input maxLength={30} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="省市区" name="provinceCityDistrict" rules={[{ required: true, message: '请选择省市区' }]}>
                <Cascader options={cascaderOptions} placeholder="请选择省市区" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="详细地址" name="detailAddress" rules={[{ required: true, message: '请输入详细地址' }]}>
                <Input placeholder="请输入详细地址" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                label="联系方式(负责人)"
                name="phoneDecimal"
                rules={[
                  { 
                    validator: async (_, value) => {
                      if (!value || !Array.isArray(value) || value.length === 0) {
                        return Promise.reject(new Error('请至少输入一个联系方式'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Form.List name="phoneDecimal">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Space key={key} align="baseline">
                          <Form.Item
                            {...restField}
                            name={[name]}
                            rules={[
                              { required: true, message: '请输入联系方式' },
                              { 
                                pattern: /^\d{11}$/, 
                                message: '手机号码必须是11位数字' 
                              },
                              {
                                validator: (_, value) => {
                                  if (!value) return Promise.resolve();
                                  
                                  // 检查是否有重复的电话号码
                                  const allValues = editForm.getFieldValue('phoneDecimal') || [];
                                  const filteredValues = allValues.filter(v => v && v.trim());
                                  const count = filteredValues.filter(v => v === value).length;
                                  
                                  if (count > 1) {
                                    return Promise.reject(new Error('联系方式不能重复'));
                                  }
                                  
                                  return Promise.resolve();
                                }
                              }
                            ]}
                          >
                            <Input placeholder="请输入手机号码" maxLength={11} />
                          </Form.Item>
                          {fields.length > 1 && (
                            <MinusCircleOutlined onClick={() => remove(name)} />
                          )}
                        </Space>
                      ))}
                      {fields.length < 10 && (
                        <Form.Item>
                          <Button 
                            type="dashed" 
                            onClick={() => add()} 
                            block
                            icon={<PlusOutlined />}
                          >
                            添加联系方式
                          </Button>
                        </Form.Item>
                      )}
                    </>
                  )}
                </Form.List>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="用户限制数量" name="userLimitQuantum" rules={[{ required: true, message: '请输入用户限制数量' }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="经度" name="lng">
                <InputNumber style={{ width: '100%' }} step={0.000001} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="纬度" name="lat">
                <InputNumber style={{ width: '100%' }} step={0.000001} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="有效开始时间" name="validStartTime" rules={[{ required: true, message: '请选择有效开始时间' }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="有效结束时间" name="validEndTime" rules={[{ required: true, message: '请选择有效结束时间' }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomerList;
